import { inject, Injectable, signal, Signal } from '@angular/core';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { BreadcrumbItem } from './models/breadcrumb.model';

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private breadcrumbsSignal = signal<BreadcrumbItem[]>([]);

  readonly breadcrumbs: Signal<BreadcrumbItem[]> = this.breadcrumbsSignal.asReadonly();

  constructor() {
    this.initializeAutoBreadcrumbs();
  }

  /**
   * Automatically build breadcrumbs from route data on navigation
   */
  private initializeAutoBreadcrumbs(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        let breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
        breadcrumbs = this.injectFeatureComparisonContext(breadcrumbs);
        breadcrumbs = this.injectCaseInvestigationBackLink(breadcrumbs);
        this.breadcrumbsSignal.set(breadcrumbs);
      });
  }

  /**
   * When on feature-comparison route, inject context segment(s) from `from` query param
   * between Home and "Feature Comparison".
   */
  private injectFeatureComparisonContext(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    if (breadcrumbs.length < 2) return breadcrumbs;
    const last = breadcrumbs[breadcrumbs.length - 1];
    if (last?.label !== 'Feature Comparison') return breadcrumbs;

    if (!this.router.url.includes('feature-comparison')) return breadcrumbs;

    const urlTree = this.router.parseUrl(this.router.url);
    const from = urlTree.queryParams['from'] ?? 'dashboard';
    const contextSegments = this.getFeatureComparisonContextSegments(from);
    if (contextSegments.length === 0) return breadcrumbs;

    const [first, ...rest] = breadcrumbs;
    return [first, ...contextSegments, ...rest];
  }

  /**
   * When on Case Investigation (detail) under employee-investigation, make "Employee Investigation"
   * link to the Case Investigation Tracker (list) so users return to the list, not the placeholder.
   */
  private injectCaseInvestigationBackLink(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    const url = this.router.url;
    if (!url.includes('/employee-investigation/case-investigation')) return breadcrumbs;
    return breadcrumbs.map((item) =>
      item.label === 'Employee Investigation'
        ? { ...item, url: '/case-investigation-tracker' }
        : item
    );
  }

  private getFeatureComparisonContextSegments(from: string): BreadcrumbItem[] {
    switch (from) {
      case 'dashboard':
        return [];
      case 'employee-variance-management':
        return [
          { label: this.translate.instant('breadcrumbs.varianceTracker'), url: '/variance-tracker' },
          { label: this.translate.instant('breadcrumbs.employeeVarianceManagement'), url: '/variance-tracker' },
        ];
      case 'case-investigation':
        return [
          { label: this.translate.instant('breadcrumbs.employeeInvestigation'), url: '/case-investigation-tracker' },
          { label: this.translate.instant('breadcrumbs.caseInvestigation'), url: '/employee-investigation/case-investigation/case-CLD18-1' },
        ];
      default:
        return [];
    }
  }

  /**
   * Recursively build breadcrumb trail from route tree
   */
  private buildBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const data: Data = child.snapshot.data;
      const label = data['breadcrumb'];

      if (label) {
        const next: BreadcrumbItem = {
          label: label,
          url: url || '/'
        };

        const last = breadcrumbs[breadcrumbs.length - 1];
        // Avoid duplicates caused by data inheritance on empty-path child routes
        if (!last || last.label !== next.label || last.url !== next.url) {
          breadcrumbs.push(next);
        }
      }

      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  /**
   * Clear all breadcrumbs
   */
  clear(): void {
    this.breadcrumbsSignal.set([]);
  }
}
